import { Camera } from '../api/models'

export function getUserInitials(profileName: string): string {
  const splitName = profileName.split(' ')
  let userInitials = ''

  for (let index = 0; index < splitName.length; index++) {
    const value = splitName[index].charAt(0).toUpperCase()
    userInitials = userInitials.concat(value)
  }

  return userInitials.toUpperCase()
}

/**
 * Converts a string value as a boolean. The following values are considered true: true, yes, TRUE, YES, 1. Everything else is considered false.
 * @param inputStr
 */
export function bool(inputStr: string | null | undefined): boolean {
  if (inputStr === undefined || inputStr === null) {
    return false
  }
  const lower = inputStr.toLowerCase().trim()
  return lower === 'true' || lower === 'yes' || lower === '1'
}

// Converts number to word (0-10)
export const numberToWord = (num: number): string => {
  const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']
  return words[num] ?? num.toString()
}

// using regex to validate a uuid string with or without dashes
export function isUuid(inputtedValue: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(inputtedValue) || /^[0-9a-f]{32}$/i.test(inputtedValue)
}

// Accepted location-search input formats, shared by the search box and route guards so they stay in sync:
// - SID: numeric only (\d+)
// - System Number: "SSI" followed by digits
// - Camera ID: a UUID (hyphenated or 32-hex) — only meaningful where camera search is enabled
export const SID_OR_SYSTEM_NUMBER_PATTERN = /^(SSI\d+|\d+)$/i
export const LOCATION_SEARCH_INPUT_PATTERN =
  /^(SSI\d+|\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i

/**
 * Whether a value matches an accepted location-search input format (SID, system number, or camera ID).
 * @param allowCameraId also accept a Camera ID (UUID); defaults to true.
 */
export function isValidLocationSearchInput(value: string, { allowCameraId = true }: { allowCameraId?: boolean } = {}): boolean {
  return (allowCameraId ? LOCATION_SEARCH_INPUT_PATTERN : SID_OR_SYSTEM_NUMBER_PATTERN).test(value)
}

/**
 * the aspect ratio in the camera object is in the form "16:9"
 * we need to convert it to "16/9" for the CSS aspect-ratio property
 * @param camera
 */
export function getAspectRatioFromCamera(camera?: Camera): string | undefined {
  return camera?.supportedFeatures?.aspectRatio?.replace(':', ' / ')
}

export function noop(): void {
  // no-op
}

export function isOdmonEnrolledCamera(camera: Camera): boolean {
  return camera.supportedFeatures.outdoorMonitoringEnabled
}

/**
 * Executes an array of asynchronous tasks with a concurrency limits
 * @returns An array of PromiseSettledResult objects representing the outcome of each task in the
 * same order as the input tasks. Each result is either:
 * `{ status: 'fulfilled', value }` or `{ status: 'rejected', reason }`
 */
export async function batchedPromiseAllSettled<T>(tasks: Array<() => Promise<T>>, limit: number): Promise<Array<PromiseSettledResult<T>>> {
  const results: Array<PromiseSettledResult<T>> = []
  while (tasks.length) {
    const batch = await Promise.allSettled(tasks.splice(0, limit).map(task => task()))
    results.push(...batch)
  }
  return results
}
